import type { SourceCardStatus } from '../../services/api'

/**
 * What a source card may link to and what each status says (MAR-173 PR 3).
 * Kept apart from `SourceCard.tsx` so the policy is importable and testable on
 * its own.
 */

/**
 * The client's copy of `ALLOWED_DEEP_LINKS`
 * (`services/chat/merchant-chat/src/capabilities/deep-links.js`), with the name
 * the link is shown as.
 *
 * ⚠️ Exact match only — no prefix, normalisation or query tolerance, the same
 * rule as the server's `assertAllowedDeepLink`. The server already refuses to
 * build anything else; this is the second wall, because a stored card is data
 * that crossed a network and a history row, and an href rendered from data is
 * the one thing a card must never do. A link not in this map renders NO link.
 * Keep it in step with `deep-links.js`; `SourceCard.test.tsx` pins it by value.
 */
export const ALLOWED_DEEP_LINKS: Readonly<Record<string, string>> = Object.freeze({
  '/ledger': 'Ledger',
  '/ledger?inbox=open': 'Inbox',
  '/knowledge': 'Knowledge',
  '/authority?tab=authority': 'Authority',
  '/authority?tab=limits': 'Limits',
})

/** The allowlisted link, or `null`. Never returns a string it was not given by the map. */
export function allowedDeepLink(link: unknown): { to: string; label: string } | null {
  if (typeof link !== 'string') return null
  if (!Object.prototype.hasOwnProperty.call(ALLOWED_DEEP_LINKS, link)) return null
  return { to: link, label: ALLOWED_DEEP_LINKS[link] }
}

/**
 * What each status says. `ok` says nothing — the output is the answer. The
 * rest are one line each, and none of them may read as "nothing exists".
 */
export const STATUS_COPY: Readonly<Record<Exclude<SourceCardStatus, 'ok'>, string>> = Object.freeze({
  partial: 'Partial: some of this could not be read',
  unavailable: 'Unavailable: Marva could not read this just now',
  not_found: 'Not found: Marva could not find this record in your store',
  invalid: 'Not read: the question did not fit what this can look up',
  refused: 'Not read: this is not something Marva can read for you here',
  cancelled: 'Not read: the answer ran out of time before this was read',
})

/** A status this build does not know is still a read that did not complete. */
export const UNKNOWN_STATUS_COPY = 'Not read: this could not be read'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/**
 * "From an earlier read · 14:31 UTC", or "From an earlier read · 24 Sep, 14:31 UTC"
 * when the read was not today (UTC). The time is the READ's, from `readAt` —
 * never the answer's. An unreadable time still says the read was earlier.
 */
export function earlierReadLabel(readAt: string, now: Date = new Date()): string {
  const at = new Date(readAt)
  if (Number.isNaN(at.getTime())) return 'From an earlier read'
  const pad = (n: number) => String(n).padStart(2, '0')
  const time = `${pad(at.getUTCHours())}:${pad(at.getUTCMinutes())} UTC`
  const sameDay =
    at.getUTCFullYear() === now.getUTCFullYear() &&
    at.getUTCMonth() === now.getUTCMonth() &&
    at.getUTCDate() === now.getUTCDate()
  return sameDay
    ? `From an earlier read · ${time}`
    : `From an earlier read · ${at.getUTCDate()} ${MONTHS[at.getUTCMonth()]}, ${time}`
}
